import { z } from 'zod'
import { CurrencyCodesSchema } from '@reward-platform/platform-config'
import { ParamsSchema, PriceRangeQuerySchema } from '../../common/query-params'
import { VendorsEnum } from '../../common'
import { CarHireExtraSelectionSchema } from '../dtos/car-hire-details-dto'
import { MembershipSchema } from '../../members'

const CarHireTravelerSchema = z.object({
  age: z.number().optional(),
  ageGroup: z.enum(['ADULT', 'CHILD', 'INFANT', 'YOUTH', 'SENIOR', 'TRAVELER']),
  dateOfBirth: z.string().optional(),
})

const amountSchema = z.object({
  amount: z.number(),
  currencyCode: CurrencyCodesSchema,
})

const AmountRangeSchema = z.object({
  min: amountSchema.optional(),
  max: amountSchema.optional(),
})

const CarHireFilterArraySchema = z.preprocess((val: unknown) => {
  if (typeof val === 'string') {
    return val.split(',')
  }
  return val
}, z.string().array())

const TransmissionTypeSchema = z.preprocess(
  (val: unknown) => (val === 'any' ? undefined : val),
  z.enum(['A', 'M']).optional()
)

/**
 * @deprecated Legacy vehicle-size query param. Use carSizes after the RPS-17171 rollout.
 * TODO(RPS-17171): Remove once the carSizes contract is fully rolled out and the FE flag is retired.
 */
const DeprecatedCarSizeFilterArraySchema = CarHireFilterArraySchema.optional().describe(
  'Deprecated legacy vehicle-size filter. Use carSizes.'
)

const CarShoppingFilterSchema = z.object({
  carCompany: z.string().optional(),
  transmissionType: TransmissionTypeSchema,
  carTypes: CarHireFilterArraySchema.optional(),
  carSize: DeprecatedCarSizeFilterArraySchema,
  carSizes: CarHireFilterArraySchema.optional(),
  priceRange: PriceRangeQuerySchema.optional(),
  pointRange: PriceRangeQuerySchema.optional(),
  fuelType: CarHireFilterArraySchema.optional(),
})

// One brand's desk, trimmed to the days the trip touches. Travels in the results-page URL so the
// availability service can re-run the opening-hours brand filter server-side.
export const VendorDeskParamSchema = z.object({
  brandId: z.string(),
  // The URL round-trip turns booleans into "true"/"false" strings; z.coerce.boolean would read
  // "false" as true, so map the strings explicitly.
  afterHoursIndicator: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
    .optional(),
  openingTimes: z
    .array(
      z.object({
        day: z.string(),
        // qs omits an empty array from the URL. A published day with no `hours` parameter is the
        // round-tripped representation of `hours: []`, not malformed opening-hours data.
        hours: z.array(z.string()).default([]),
      })
    )
    .optional(),
})

export type VendorDeskParam = z.infer<typeof VendorDeskParamSchema>

export const SummaryCarHireLocationSearchSchema = z.object({
  id: z.string(),
  type: z.string(),
  // Tells a genuine return trip from a cross-country one-way that reuses a station code (AT5 is
  // both Athens Downtown, GR and Atlanta/Tucker, US).
  countryCode: z.string().optional(),
  vendorDesks: z.array(VendorDeskParamSchema).optional(),
})

export const CarHireSearchParamsSchema = ParamsSchema.extend({
  dropOffLocation: SummaryCarHireLocationSearchSchema.optional(),
  pickUpLocation: SummaryCarHireLocationSearchSchema,
  searchType: z.string().optional(),
  travelers: z.array(CarHireTravelerSchema),
  shoppingFilter: CarShoppingFilterSchema.optional(),
  vendorBrandId: VendorsEnum,
  // Every brand the pick-up and drop-off have in common. The availability service asks each of
  // them separately on a one-way trip, because upstream keeps only one brand per request.
  vendorBrandIds: z.array(VendorsEnum).optional(),
  membership: MembershipSchema.optional(),
  membershipCode: z.string().optional(),
  scrollRef: z.string().optional(),
})

export const CarHireSearchFormParamsSchema = ParamsSchema.extend({
  pickUpLocation: SummaryCarHireLocationSearchSchema,
  dropOffLocation: SummaryCarHireLocationSearchSchema.optional(),
  pickUpSearchString: z.string().optional(),
  dropOffSearchString: z.string().optional(),
  searchType: z.string().optional(),
  travelers: z.array(CarHireTravelerSchema),
  shoppingFilter: CarShoppingFilterSchema.optional(),
  vendorBrandId: VendorsEnum,
  vendorBrandIds: z.array(VendorsEnum).optional(),
  membership: MembershipSchema.optional(),
  membershipCode: z.string().optional(),
  scrollRef: z.string().optional(),
})

export const CarHireDetailsQuerySchema = z.object({
  bundleId: z.string().min(1),
  recommendationId: z.string().min(1),
  correlationId: z.string().min(1),
  searchType: z.string(),
  travelers: z.array(CarHireTravelerSchema),
  dropOffDate: z.string(),
  pickUpDate: z.string(),
  dropOffLocation: z.string(),
  pickUpLocation: z.string(),
  vendorBrandId: VendorsEnum,
  membership: MembershipSchema.optional(),
  membershipCode: z.string().optional(),
})

export const CarHireDetailsParamsSchema = CarHireDetailsQuerySchema.omit({
  searchType: true,
  dropOffDate: true,
  pickUpDate: true,
  dropOffLocation: true,
  pickUpLocation: true,
  membershipCode: true,
})

export const CarHireExtrasDetailsParamsSchema = z.object({
  bundleId: z.string().min(1),
  recommendationId: z.string().min(1),
  vendorBrandId: VendorsEnum,
  extraSelection: CarHireExtraSelectionSchema,
  membership: MembershipSchema.optional(),
})

export type CarHireSearchFormParams = z.infer<typeof CarHireSearchFormParamsSchema>
export type CarHireSearchParams = z.infer<typeof CarHireSearchParamsSchema>
export type CarHireDetailsQuery = z.infer<typeof CarHireDetailsQuerySchema>
export type CarHireDetailsParams = z.infer<typeof CarHireDetailsParamsSchema>
export type CarShoppingFilterQuery = z.infer<typeof CarShoppingFilterSchema>
export type AmountRange = z.infer<typeof AmountRangeSchema>
export type CarHireExtrasDetailsParams = z.infer<typeof CarHireExtrasDetailsParamsSchema>
